import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import DeleteButton from '../../DeleteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import ScenarioSide from './scenario-side';

export const MacBotGotoForm = ({
  setShow,
  itemData,
  parentId,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  tree,
}: {
  setShow?: (item: boolean) => void;
  parentId: string | null;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const [treeData, setTreeData] = useState<any[]>([]);

  useEffect(() => {
    setTreeData(tree);
  }, [tree]);

  interface IGOtoForm extends INoteForm {
    BlockTitle: string;
    Goto: string;
  }

  const schema = Yup.object({
    Goto: Yup.string().required(t(AlertsType.ISKBKO024)),
    ...getNoteSchema(t),
  });

  useEffect(() => {
    if (itemData) {
      if (itemData?.title) setValue('BlockTitle', itemData?.title);
      if (itemData?.next) setValue('Goto', itemData?.next);
      setValue('notes', itemData?.notes);
    }
  }, [itemData]);

  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    getValues,
    watch,
    control,
  } = useForm<IGOtoForm>({
    defaultValues: {
      BlockTitle: '',
      Goto: '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const ButtonSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.Goto,
        title: data.Goto,
        next: data.Goto,
        parent: itemData.parent,
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.Goto,
        title: data.Goto,
        next: data.Goto,
        parent: parentId,
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  return (
    <ScenarioSide onSubmit={handleSubmit(ButtonSubmit)} size="md">
      <ScenarioHeader
        type={MacBotActionKind.ShowGotoForm}
        onClose={() => {
          reset();
        }}
      />

      <Row className="tw-mt-5 ">
        <Col>
          <Row className="m-0 pb-3">
            <Col lg="12" className="px-3">
              <Form.Group>
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotGotoForm/l-03')} </Form.Label>
                <Form.Select
                  translate="no"
                  {...register('Goto')}
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  aria-label={t('MacBotGotoForm/l-04')}
                >
                  {tree
                    // ?.filter((t) => t.parent)
                    .map(dr =>
                      dr.type === BlockType.Response ? (
                        <option key={dr.id} value={dr.id}>
                          {dr.block_id} - {dr.title}
                        </option>
                      ) : null
                    )}
                </Form.Select>
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.Goto?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="tw-flex flex-row tw-justify-between tw-items-center">
        <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
          <DeleteButton deleteHandler={deleteHandler} />
          {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
          <AddNoteButton noteFormMethods={{ getValues, setValue }} />
        </div>
        <div className="tw-flex tw-justify-end">
          <PSButton id="btn-submit-goto" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
        </div>
      </div>
      <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
    </ScenarioSide>
  );
};
